import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, ChevronDown, LogOut, User, LayoutDashboard, Shield, ArrowRight } from 'lucide-react';
import NotificationCenter from './NotificationCenter';
import LaunchCampaignChoiceModal from './LaunchCampaignChoiceModal';

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const navLinks = user
    ? [
        // Campaigns + Case Studies hidden while empty — re-enable when populated.
        { path: user.role === 'brand' ? '/portal?tab=help' : '/dashboard?tab=help', label: 'Help' },
      ]
    : [
        { path: '/for-brands', label: 'How It Works' },
      ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setProfileOpen(false);
  };

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#090E33]/80 border-b border-white/10" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5" data-testid="navbar-logo">
            <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/pflz8b2v_freddy-logo-symbol.png" alt="Freddy Media" className="h-8 w-8" />
            <span className="font-display text-xl font-bold tracking-tight">
              <span className="text-[#FF4343]">Freddy</span>
              <span className="text-white">Leads</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(l => (
              <Link
                key={l.path}
                to={l.path}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(l.path) ? 'text-[#FF4343]' : 'text-[#9CA3AF] hover:text-white'
                }`}
              >
                {l.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-4">
            {!user ? (
              <>
                <Link
                  to="/login?tab=register&role=executive"
                  data-testid="nav-apply-btn"
                  className="text-sm font-medium text-[#9CA3AF] hover:text-white transition-colors whitespace-nowrap"
                >
                  Join Executive Roster
                </Link>
                <Link
                  to="/login"
                  data-testid="nav-login-btn"
                  className="text-sm font-medium text-[#9CA3AF] hover:text-white transition-colors"
                >
                  Log In
                </Link>
                <button
                  type="button"
                  onClick={() => setBookOpen(true)}
                  data-testid="nav-book-walkthrough-btn"
                  className="rounded-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] hover:border-[#E03030] px-5 py-2 text-xs lg:text-sm font-bold transition-all duration-300 whitespace-nowrap inline-flex items-center gap-1.5"
                >
                  Book a Walkthrough <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <NotificationCenter />
                <div className="relative">
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  data-testid="nav-profile-menu"
                  className="flex items-center gap-2 text-sm font-medium text-white hover:text-[#FF4343] transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-[#111827] border border-white/20 flex items-center justify-center overflow-hidden">
                    {user.headshot_url ? (
                      <img src={user.headshot_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-4 h-4 text-[#9CA3AF]" />
                    )}
                  </div>
                  <span className="max-w-[120px] truncate">{user.name}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-56 glass shadow-lg py-2" data-testid="profile-dropdown">
                    {isAdmin && (
                      <Link to="/admin" onClick={() => setProfileOpen(false)} className="flex items-center gap-3 px-4 py-2 text-sm text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-colors">
                        <Shield className="w-4 h-4" /> Admin Panel
                      </Link>
                    )}
                    <Link to="/dashboard" onClick={() => setProfileOpen(false)} className="flex items-center gap-3 px-4 py-2 text-sm text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-colors">
                      <LayoutDashboard className="w-4 h-4" /> Dashboard
                    </Link>
                    <button onClick={handleLogout} data-testid="nav-logout-btn" className="w-full flex items-center gap-3 px-4 py-2 text-sm text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-colors">
                      <LogOut className="w-4 h-4" /> Log Out
                    </button>
                  </div>
                )}
              </div>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-white" data-testid="mobile-menu-btn">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden glass border-t border-white/10 px-6 py-4 space-y-3">
          {navLinks.map(l => (
            <Link key={l.path} to={l.path} onClick={() => setMobileOpen(false)}
              className={`block text-sm font-medium py-2 ${isActive(l.path) ? 'text-[#FF4343]' : 'text-[#9CA3AF]'}`}>
              {l.label}
            </Link>
          ))}
          {!user ? (
            <>
              <button type="button" onClick={() => { setMobileOpen(false); setBookOpen(true); }}
                data-testid="mobile-nav-book-btn"
                className="block w-full text-left text-sm font-bold text-[#FF4343] py-2">
                Book a Walkthrough →
              </button>
              <Link to="/login" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-[#9CA3AF] py-2">
                Log In
              </Link>
              <Link to="/login?tab=register&role=executive" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-[#9CA3AF] py-2">
                Join Executive Roster
              </Link>
            </>
          ) : (
            <>
              <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-[#9CA3AF] py-2">Dashboard</Link>
              {isAdmin && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-[#9CA3AF] py-2">Admin</Link>}
              <button onClick={handleLogout} className="text-sm font-medium text-[#9CA3AF] py-2">Log Out</button>
            </>
          )}
        </div>
      )}
      <LaunchCampaignChoiceModal open={bookOpen} onClose={() => setBookOpen(false)} source="navbar" />
    </nav>
  );
}
